import React, { useMemo, useState } from 'react';

import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { DataTableColumnsSelector } from '@/components/data-table/data-table-columns-selector';
import { DataTableFiltersV2, useTableFilters } from '@/components/data-table/filters-v2';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { useDataTable } from '@/hooks/use-data-table';
import { usePersistedTableFilters } from '@/hooks/use-persisted-table-filters';
import { TableIds } from '@/lib/constants/table-ids';
import { PageTemplate, usePermissions } from '@/modules/common';
import { ApiError } from '@/modules/common/api/errors';
import { RefreshData } from '@/modules/common/components/organisms/refresh-data';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { FcmNotificationType } from '@/modules/notifications';
import { useFcmListRefresh } from '@/modules/notifications/hooks';
import { AuthenticatedRoutes } from '@/router/routes';

import { TeamMembersTable } from '../components';
import { DeleteTeamMembersModal } from '../components/modals/delete-team-members-modal';
import { getTeamFiltersConfig, getTeamTableColumns } from '../constants';
import {
  TEAM_FILTER_KEY_STATUS,
  TEAM_TABLE_FILTER_KEYS,
  TEAM_TABLE_FILTERS_STORAGE_KEY,
} from '../constants/team-filter-keys';
import { useCustomAttributes } from '../hooks/use-custom-attributes';
import { useTeamMembers } from '../hooks/use-team-members';
import { MemberStatus, TeamMemberListItem } from '../types/team-member';

// Default status filter on first visit: all statuses except Placeholder and Archived.
const DEFAULT_TEAM_STATUS_FILTER = [
  MemberStatus.Active,
  MemberStatus.Invited,
  MemberStatus.Expired,
  MemberStatus.Basic,
];

export const TeamPage = () => {
  const { t } = useTranslation(['authenticated.team']);
  const navigate = useNavigate();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  usePersistedTableFilters({
    storageKey: TEAM_TABLE_FILTERS_STORAGE_KEY,
    filterKeys: TEAM_TABLE_FILTER_KEYS,
    defaultFilters: { [TEAM_FILTER_KEY_STATUS]: DEFAULT_TEAM_STATUS_FILTER },
  });

  const { data: customAttributes } = useCustomAttributes();
  const filtersConfig = useMemo(() => getTeamFiltersConfig(customAttributes), [customAttributes]);
  const filterState = useTableFilters(filtersConfig);
  const appliedFilters = filterState.activeFilters;
  const columns = useMemo(() => getTeamTableColumns(), []);

  const { data, isLoading, isFetching, error, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useTeamMembers({
      filters: appliedFilters,
    });

  const { hasPendingRefresh, isRefreshing, refresh } = useFcmListRefresh({
    notificationType: FcmNotificationType.TEAM_MEMBER_UPDATED,
    queryKey: ['team-members'],
    isFetching,
  });

  const { table, saveColumnSizing } = useDataTable<TeamMemberListItem>({
    data: data?.items || [],
    columns,
    getRowId: row => String(row.id),
    tableId: TableIds.TEAM_MEMBERS,
    initialState: {
      columnPinning: { left: ['select', 'name'] },
    },
  });

  const selectedRows = table.getSelectedRowModel().rows.map(row => row.original);

  const buildRowHref = (member: TeamMemberListItem) =>
    generatePath(AuthenticatedRoutes.TeamMemberDetails, { memberId: member.id.toString() });

  const handleRowClick = (member: TeamMemberListItem) => {
    navigate(buildRowHref(member));
  };

  const { hasPermission } = usePermissions();
  const isCreateMembersEnabled = hasPermission(OpsiumPolicy.TeamMembersCreate);
  const isDeleteMembersEnabled = hasPermission(OpsiumPolicy.TeamMembersDelete);
  const isEditMembersEnabled = hasPermission(OpsiumPolicy.TeamMembersEdit);

  const handleAddMemberClick = () => navigate(AuthenticatedRoutes.TeamAddMember);

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    table.resetRowSelection();
  };

  return (
    <PageTemplate
      title={t('title')}
      useStickyHeader
      className="p-0"
      headerClassName="items-start bg-surface px-4 pt-4 shadow-[0_1px_4px_0_hsl(var(--border))]"
      contentClassName="relative"
      rightActions={
        <div className="flex gap-2">
          {isCreateMembersEnabled && (
            <Button size="sm" onClick={handleAddMemberClick}>
              <CirclePlus />
              {t('addMember')}
            </Button>
          )}
          {/* TODO import is not supported on MVP */}
          {/*<Button size="sm">
            <Download />
            {t('importMembers')}
          </Button>*/}
        </div>
      }
    >
      <Separator className="ml-[-16px] w-[calc(100%+32px)]" />
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pt-4">
        {hasPendingRefresh && (
          <RefreshData
            onRefresh={refresh}
            isPending={isRefreshing}
            dataUpdatedAt={null}
            description={t('refreshBanner.description')}
            buttonTitle={t('refreshBanner.buttonTitle')}
            containerClassName="mb-4"
          />
        )}
        {/* Sticky filters section */}
        <div className="mb-4 flex shrink-0 items-start justify-between gap-2 pt-1">
          <DataTableFiltersV2
            filtersConfig={filtersConfig}
            filterState={filterState}
            tableId={TableIds.TEAM_MEMBERS}
            className="flex flex-wrap gap-2"
          />
          <div className="mt-1 flex items-center gap-2">
            {selectedRows.length > 0 && isDeleteMembersEnabled && (
              <>
                {/*TODO uncomment when implement bulk archivation*/}
                {/*isEditMembersEnabled && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={selectedRows.some(employee => employee.isChampion)}
                      >
                        {t('common:archive')}
                      </Button>
                    </TooltipTrigger>

                    {selectedRows.some(employee => employee.isChampion) ? (
                      <TooltipPortal>
                        <TooltipContent>
                          <p>{t('authenticated.team:cantArchiveChampionTooltip')}</p>
                        </TooltipContent>
                      </TooltipPortal>
                    ) : undefined}
                  </Tooltip>
                </TooltipProvider>
              )*/}
                <Tooltip>
                  <TooltipTrigger>
                    <Button
                      size="sm"
                      variant="outlineDestructive"
                      disabled={selectedRows.some(employee => employee.isChampion)}
                      onClick={() => setIsDeleteModalOpen(true)}
                    >
                      {t('common:delete')}
                    </Button>
                  </TooltipTrigger>

                  {selectedRows.some(employee => employee.isChampion) ? (
                    <TooltipPortal>
                      <TooltipContent>
                        <p>{t('authenticated.team:cantDeleteChampionTooltip')}</p>
                      </TooltipContent>
                    </TooltipPortal>
                  ) : undefined}
                </Tooltip>
              </>
            )}
            {selectedRows.length > 0 && (
              <Button size="sm" variant="ghost" onClick={() => table.resetRowSelection()}>
                {t('common:clear')}
              </Button>
            )}
            <DataTableColumnsSelector table={table} />
          </div>
        </div>

        {/* Total count */}
        <p className="mb-4 shrink-0 text-xs text-muted-foreground">
          {t('authenticated.team:common.allMembers')}: {data?.pagination?.total ?? 0}
        </p>

        {/* Scrollable table */}
        <TeamMembersTable
          table={table}
          onRowClick={handleRowClick}
          getRowHref={buildRowHref}
          isLoading={isLoading || isFetchingNextPage || isRefreshing}
          hasMore={hasNextPage}
          onLoadMore={() => fetchNextPage()}
          error={error instanceof ApiError ? t(error.code) : error?.message}
          isAnyFilterApplied={Object.values(appliedFilters).some(value =>
            Array.isArray(value) ? value.length > 0 : !!value
          )}
          totalCount={data?.pagination?.total ?? 0}
          stickyHeader
          fixedHeight
          tableId={TableIds.TEAM_MEMBERS}
          onResizeEnd={saveColumnSizing}
        />

        {/* Modals */}
        <DeleteTeamMembersModal
          teamMembersForDelete={selectedRows}
          open={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onSuccess={handleCloseDeleteModal}
        />
      </div>
    </PageTemplate>
  );
};
